import React, { useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { GoogleMap, MarkerF, CircleF } from '@react-google-maps/api';
import useQueryGetMe from '@/api/auth/getMe.ts';
import useQueryGetUsers, { AlertStatus, DeviceStatus, IUser } from '@/api/user/getUsers.ts';
import clustering from 'density-clustering';
import ReactDOMServer from 'react-dom/server';
import MarkerWindow from '@/pages/dashboard/MarkerWindow.tsx';

interface IProps {
    className?: string;
}

export enum MarketTYPE {
    CLUSTER,
    SINGLE
}

export interface IMarker {
    id: number;
    type?: MarketTYPE;
    location?: IMarketLocation;
    alertStatus?: AlertStatus;
    deviceStatus?: DeviceStatus;
    users: IUser[];
}

export interface IMarketLocation {
    latitude?: number;
    longitude?: number;
    accuracy?: number;
    timestamp?: string;
}

export interface IMarkerInfo extends IMarker {
    locationName?: string;
    render?: any;
    address: string;
}

const CACHE__ZOOM_LEVEL = 'dashboard_map__zoom';
const CACHE__CENTER_X = 'dashboard_map__center__longitude';
const CACHE__CENTER_Y = 'dashboard_map__center__latitude';

const DashboardMapDefault = ({ className }: IProps, ref: any) => {
    const [map, setMap] = useState<any>(null);
    const [userInfo, setUserInfo] = useState<IMarkerInfo>();
    const [selectedMarker, setSelectedMarker] = useState<IMarker | null>(null);
    const [zoomLevel, setZoomLevel] = useState(0);
    const { data: users } = useQueryGetUsers();
    const { data: me } = useQueryGetMe();
    const [infoWindowInstance, setInfoWindowInstance] = useState<google.maps.InfoWindow | null>(null);

    useImperativeHandle(ref, () => ({
        setLocation(latitude: number, longitude: number, zoom?: number) {
            map?.panTo({ lat: latitude, lng: longitude });
            if (zoom) {
                map?.setZoom(zoom);
            }
        }
    }));

    const openUserInfo = (info?: IMarker) => {
        if (!info || userInfo?.id === info?.id) {
            setUserInfo(undefined);
            setSelectedMarker(null);
        } else {
            const { latitude = 0, longitude = 0 } = info?.location || {};
            const isMasking = me?.email === 'dbd_cm@clomental.com';

            if (latitude === 0 && longitude === 0) {
                const infoWithoutAddress: IMarkerInfo = {
                    ...info!,
                    address: 'Coordinates not available',
                };

                infoWithoutAddress.render = ReactDOMServer.renderToString(<MarkerWindow info={infoWithoutAddress} isMasking={isMasking} />);
                setUserInfo(infoWithoutAddress);
                setSelectedMarker(info);
                return;
            }

            const geocoder = new google.maps.Geocoder();
            geocoder.geocode(
                { location: { lat: latitude, lng: longitude } },
                (results, status) => {
                    if (status === 'OK' && results && results[0]) {
                        const infoWithAddress: IMarkerInfo = {
                            ...info!,
                            address: results[0].formatted_address
                        };

                        infoWithAddress.render = ReactDOMServer.renderToString(<MarkerWindow info={infoWithAddress} isMasking={isMasking} />);
                        setUserInfo(infoWithAddress);
                        setSelectedMarker(info);
                    } else {
                        const infoWithoutAddress: IMarkerInfo = {
                            ...info!,
                            address: 'Geocoding failed',
                        };

                        infoWithoutAddress.render = ReactDOMServer.renderToString(<MarkerWindow info={infoWithoutAddress} isMasking={isMasking} />);
                        setUserInfo(infoWithoutAddress);
                        setSelectedMarker(info);
                    }
                }
            );
        }
    };

    useEffect(() => {
        if (!map) {
            return;
        }

        if (infoWindowInstance) {
            infoWindowInstance.close();
        }

        if (userInfo) {
            const infoWindowContent = `
            <div style="font-family: 'Noto Sans KR', sans-serif; font-weight: 400;">
                ${userInfo?.render}
            </div>
        `;

            const newInfoWindowInstance = new google.maps.InfoWindow({
                content: infoWindowContent,
            });
            newInfoWindowInstance.setPosition({ lat: userInfo.location?.latitude || 0, lng: userInfo.location?.longitude || 0 });
            newInfoWindowInstance.open(map);
            setInfoWindowInstance(newInfoWindowInstance);
        }
    }, [userInfo, map]);


    const markers: IMarker[] = useMemo(() => {
        const filtered = users?.filter((item) => item?.device?.status) || [];
        const radius = new Map<number, number>();
        radius.set(21, 0.000005);
        radius.set(20, 0.00001);
        radius.set(19, 0.00002);
        radius.set(18, 0.00005);
        radius.set(17, 0.0001);
        radius.set(16, 0.0002);
        radius.set(15, 0.0005);
        radius.set(14, 0.001);
        radius.set(13, 0.002);
        radius.set(12, 0.005);
        radius.set(11, 0.01);
        radius.set(10, 0.02);
        radius.set(9, 0.05);
        radius.set(8, 0.1);
        radius.set(7, 0.2);
        radius.set(6, 0.5);
        radius.set(5, 1);
        radius.set(4, 2);
        radius.set(3, 5);
        radius.set(2, 10);
        radius.set(1, 20);

        const dbscan = new clustering.DBSCAN();
        const clusters = dbscan.run(
            filtered?.map((item) => [item?.location?.longitude || 0, item?.location?.latitude || 0]) || [],
            radius.get(zoomLevel) ?? 0.000005,
            2
        );

        const markers: IMarker[] = [];

        clusters?.map((ids) => {
            const item = filtered[ids[0]];
            const filteredUsers = ids?.map((id) => filtered[id]);

            let alertStatus: AlertStatus;
            if (filteredUsers?.find((item) => item?.alertStatus === AlertStatus.EMERGENCY)) {
                alertStatus = AlertStatus.EMERGENCY;
            } else if (filteredUsers?.find((item) => item?.alertStatus === AlertStatus.BIO)) {
                alertStatus = AlertStatus.BIO;
            } else {
                alertStatus = AlertStatus.NONE;
            }

            markers?.push({
                id: item?.id,
                alertStatus: alertStatus,
                location: {
                    latitude: item?.location?.latitude,
                    longitude: item?.location?.longitude,
                    accuracy: item?.location?.accuracy,
                    timestamp: item?.location?.timestamp
                },
                type: MarketTYPE.CLUSTER,
                users: filteredUsers
            });
        });

        dbscan?.noise?.map((id) => {
            const item = filtered[id];
            markers?.push({
                id: item?.id,
                alertStatus: item?.alertStatus,
                location: {
                    latitude: item?.location?.latitude,
                    longitude: item?.location?.longitude,
                    accuracy: item?.location?.accuracy,
                    timestamp: item?.location?.timestamp
                },
                type: MarketTYPE.SINGLE,
                users: [item]
            });
        });

        markers.sort((a, b) => {
            const priority = {
                [AlertStatus.EMERGENCY]: 2,
                [AlertStatus.BIO]: 1,
                [AlertStatus.NONE]: 0,
            };

            const priorityA = priority[a.alertStatus ?? AlertStatus.NONE];
            const priorityB = priority[b.alertStatus ?? AlertStatus.NONE];

            return priorityA - priorityB;
        });

        return markers;
    }, [users, zoomLevel]);

    return (
        <div className={`${className}`}>
            <div style={{ width: '100%', height: '100%' }}>
                {me && (
                    <GoogleMap
                        onLoad={(mapInstance) => setMap(mapInstance)}
                        center={{
                            lat: Number(localStorage.getItem(CACHE__CENTER_Y)) || me?.latitude || 37.5759,
                            lng: Number(localStorage.getItem(CACHE__CENTER_X)) || me?.longitude || 126.9768
                        }}
                        zoom={Number(localStorage.getItem(CACHE__ZOOM_LEVEL)) || 18}
                        onZoomChanged={() => {
                            setZoomLevel(map?.getZoom() || 18);
                            localStorage.setItem(CACHE__ZOOM_LEVEL, map?.getZoom()?.toString() || '18');
                        }}
                        onCenterChanged={() => {
                            const center = map?.getCenter();
                            if (center) {
                                localStorage.setItem(CACHE__CENTER_X, center.lng().toString());
                                localStorage.setItem(CACHE__CENTER_Y, center.lat().toString());
                            }
                        }}
                        mapContainerStyle={{ width: '100%', height: '100%' }}
                    >
                        {markers?.map((item) => (
                            <MarkerF
                                key={`marker_${item?.id}`}
                                position={{ lat: item?.location?.latitude || 0, lng: item?.location?.longitude || 0 }}
                                icon={{
                                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                                        (() => {
                                            let color: string = '#2966F4';
                                            let shadowColor: string = '#2966F4';
                                            switch (item?.alertStatus) {
                                                case AlertStatus.EMERGENCY:
                                                    color = '#FF003D';
                                                    shadowColor = '#FF003D';
                                                    break;
                                                case AlertStatus.BIO:
                                                    color = '#FF7D33';
                                                    shadowColor = '#FF7D33';
                                                    break;
                                                default:
                                                    color = '#2966F4';
                                                    shadowColor = '#2966F4';
                                                    break;
                                            }

                                            return `
                                                <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50">
                                                    <circle cx="25" cy="25" r="20" fill="${color}" stroke="white" stroke-width="3"/>
                                                    <text x="25" y="30" fill="white" font-size="20" font-family="Arial" text-anchor="middle">
                                                        ${item?.users?.length}
                                                    </text>
                                                    <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                                                        <feDropShadow dx="0" dy="0" stdDeviation="4" flood-color="${shadowColor}"/>
                                                    </filter>
                                                </svg>
                                            `;
                                        })() as string
                                    )}`
                                }}
                                onClick={() => openUserInfo(item)}
                            />
                        ))}

                        {selectedMarker && (
                            <CircleF
                                center={{ lat: selectedMarker.location?.latitude || 0, lng: selectedMarker.location?.longitude || 0 }}
                                radius={selectedMarker.location?.accuracy || 100}
                                options={{
                                    fillColor: '#78bfffcc',
                                    strokeColor: '#78bfffcc',
                                    strokeWeight: 1,
                                }}
                            />
                        )}
                    </GoogleMap>
                )}
            </div>
        </div>
    );
};

export default React.forwardRef(DashboardMapDefault);
